/**
 * Gold Loan Insert API Service
 * Handles gold loan application submission
 *
 * Refactored from: 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/glInsertApi.ts
 * Logic preserved AS-IS, imports updated for monorepo structure
 */

import { PROD_CODES } from "@repo/shared-utils/cart";
import { getUtmParamsFromCookie } from "@repo/shared-utils/common-utils";
import { getOrDefaultValue } from "@repo/shared-utils/insert-params";
import { request } from "../core/request";
import { EVENT_CODE, OPERATION_IDS } from "./constants";
import { IGLInsertData, IGLInsertRequest } from "../types/glTypes";

/**
 * GL Insert Response Type
 * Note: Original code referenced IGlInsertType from component types
 * Using generic object type for now - can be refined based on actual response structure
 */
export interface IGLInsertResponse {
  statusCode?: string;
  description?: string;
  appId?: string;
  leadId?: string;
  [key: string]: unknown;
}

/**
 * Insert gold loan application
 *
 * @param data - Gold loan application request data
 * @returns Promise with gold loan insert response or null on error
 *
 * @example
 * ```typescript
 * const response = await glInsert({
 *   mobileNo: "9876543210",
 *   journeyName: "GL_FRESH",
 *   goldWeight: "50",
 *   loanAmt: "100000"
 * });
 * ```
 */
export const glInsert = async (data: IGLInsertData): Promise<IGLInsertResponse | null> => {
  try {
    const utm = getUtmParamsFromCookie();

    const reqData: IGLInsertRequest = {
      ...data,
      id: getOrDefaultValue(data.id),
      eventCode: getOrDefaultValue(data?.eventCode, EVENT_CODE.GOLD_LOAN),
      saveForLaterFlag: data?.saveForLaterFlag || false,
      utmContent: getOrDefaultValue(utm?.utmContent as string),
      utmSource: getOrDefaultValue(utm?.utmSource as string),
      utmCampaign: getOrDefaultValue(utm?.utmCampaign as string),
      utmMedium: getOrDefaultValue(utm?.utmMedium as string),
    };
    const response = (await request({
      operationId: OPERATION_IDS.glInsert,
      requestData: {
        ...reqData,
        apiVersion: reqData.journeyName === PROD_CODES.GLFRESH?.value ? "" : "v5",
      },
      additionalHeaders: {
        apiVersion: "v4",
      },
    })) as IGLInsertResponse;

    return response;
  } catch (error) {
    console.error(error);
    return null;
  }
};
